<template>
  <PopupWrapper @on-hide="onHide" :title="state.item?.name" :ref="(el: any) => popup = el" class="popup-float" :hide-close-btn="false">
    <div class="lootbox-info">
      <div class="lootbox-wrap" ref="wrapEl">
        <div class="lootbox-marker"/>
        <div ref="trackEl" class="lootbox-track">
          <div v-for="(item, i) in loopedItems" :key="i" class="lootbox-card"
            :style="{background: `linear-gradient(180deg, ${item.color} 0%, #18181B 100%)`}"
            :class="[state.winIndex !== null && item.index !== state.winIndex ? 'inactive' : '', state.winIndex !== null && item.index === state.winIndex ? 'win' : '']">
            <img class="icon" :src="item.iconUrl" />
            <div class="coins">
              <img src="/img/coins/gold_coin.svg">
              <span>{{ numberFormatter.formatNumber(item.sum) }}</span>
            </div>
          </div>
        </div>
      </div>
      <button v-if="!state.isOpening" @click.prevent="buy" class="btn btn-lg btn-primary w-100">{{ $t('open_for', {price: numberFormatter.formatMoney(state.item.price)})}}</button>
      <button v-else disabled class="btn btn-lg btn-secondary w-100">
        <svg-icon icon="loader-icon"/>

        {{ $t('opening')}}
      </button>
      <div class="lootbox-content">
        <h2>{{$t('whats_inside')}}</h2>
        <div class="items">
          <div v-for="(item, index) in lootBox?.items" class="item">
            <span class="coins"><img src="/img/coins/gold_coin.svg"> {{numberFormatter.formatNumber(item.sum)}}</span>
            <span class="type" :style="{color:item.color}">{{ item.name }} · {{ numberFormatter.formatNumber(item.weight, 1) }}%</span>
          </div>
        </div>
      </div>
    </div>
  </PopupWrapper>
</template>
<script setup lang="ts">
import PopupWrapper from "@/components/PopupWrapper.vue";
import {reactive} from "@vue/reactivity";
import {computed, nextTick, onBeforeUnmount, ref} from "vue";
import StoreItemDTO from "@/models/StoreItemDTO";
import StoreLootBoxDTO from "@/models/StoreLootBoxDTO";
import {numberFormatter} from "@/service/NumberFormatter";
import MoneyDTO from "@/models/MoneyDTO";
import {useStore} from "vuex";
import {useRouter} from "vue-router";
import {AppEvents} from "@/models/enum/AppEvents";
import {api} from "@/service/APIClient";
import {PurchaseStatus} from "@/models/enum/PurchaseStatus";
import {useNotification} from "@kyvg/vue3-notification";
import {$t} from "@/i18n";
import SvgIcon from "@/components/SvgIcon.vue";

const notification = useNotification()
const store = useStore()
const router = useRouter()

const state = reactive({
  item: null as StoreItemDTO|null,
  isOpening: false as boolean,
  winIndex: null as number|null
})

const IDLE_SPEED = 120;
const COPIES = 3

const lootBox = computed(() => {
  return state.item?.content as StoreLootBoxDTO|null
})

const loopedItems = computed(() => {
  if (!state.item) {
    return []
  }

  return Array.from({length: COPIES}, () => {
    return (state.item?.content as StoreLootBoxDTO).items.map((item, index) => {
      return item
    })
  }
  ).flat()
})

const trackEl = ref<HTMLElement | null>(null)
const wrapEl = ref<HTMLElement | null>(null)
let offset = 0
let setWidth = 0
let rafId: number | null = null
let lastTs = 0
let isPaused = false
let cardStep = 0
let wrapWidth = 0

function measure() {
  const t = trackEl.value, w = wrapEl.value
  if (!t || !w) return
  setWidth  = t.scrollWidth / COPIES
  cardStep  = setWidth / (state.item?.content as StoreLootBoxDTO).items.length
  wrapWidth = w.clientWidth
}

type Roll = {
  startOffset: number
  targetOffset: number
  startTs: number
  duration: number
  targetIndex: number
} | null
let roll: Roll = null

function easeOutQuart(t: number) {
  return 1 - Math.pow(1 - t, 4)
}

function startRoll(chosenIdx: number, duration = 6000, minSpins = 5) {
  console.log("roll start", chosenIdx)
  measure()

  const minimalOffset = chosenIdx * cardStep + cardStep / 2 - wrapWidth / 2
  const tail = (((minimalOffset - offset) % setWidth) + setWidth) % setWidth
  const target = offset + minSpins * setWidth + tail

  roll = {
    startOffset: offset,
    targetOffset: target,
    startTs: performance.now(),
    duration,
    targetIndex: chosenIdx,
  }
}

function frame(ts: number) {
  if (!lastTs) lastTs = ts
  const dt = (ts - lastTs) / 1000
  lastTs = ts

  const el = trackEl.value
  if (el && setWidth > 0) {
    if (roll) {
      const t = Math.min(1, (ts - roll.startTs) / roll.duration)
      const eased = easeOutQuart(t)
      offset = roll.startOffset + (roll.targetOffset - roll.startOffset) * eased

      if (t >= 1) {
        offset = roll.targetOffset
        const idx = roll.targetIndex
        roll = null
        isPaused = true
        state.isOpening = false
        state.winIndex = idx
        const text = $t('amount_added_to_balance', {amount: numberFormatter.formatNumber((state.item?.content as StoreLootBoxDTO).items[state.winIndex].sum)});
        notification.notify({type: 'success', title: `<div class="d-flex align-items-center gap-1"><img src="/img/coins/gold_coin.svg"> ${text}</div>`, duration: 1500})
      }
    } else if (!isPaused) {
      offset += IDLE_SPEED * dt
    }
    const display = ((offset % setWidth) + setWidth) % setWidth
    el.style.transform = `translate3d(${-display}px, 0, 0)`
  }

  rafId = requestAnimationFrame(frame)
}


const show = async (item: StoreItemDTO) => {
  state.item = item
  state.isOpening = false
  state.winIndex = null
  isPaused = false
  popup.value?.show();
  await nextTick()
  measure()
  rafId = requestAnimationFrame(frame)
}

const hide = () => {
  popup.value?.hide()
}

const onHide = () => {
  if (rafId) cancelAnimationFrame(rafId)
}

const buy = () => {
  if (!store.getters.isLoggedIn) {
    router.push({name: 'login'})
    return;
  }

  state.winIndex = null
  isPaused = false
  state.isOpening = true
  store.dispatch('emit', {event: AppEvents.BUY_ITEM, data: state.item})
}

const events = store.subscribeAction({
  before: async (action) => {
    if (action.type !== 'emit') {
      return;
    }
    switch (action.payload?.event) {
      case AppEvents.PURCHASE_CANCELED:
        state.isOpening = false;
        break;
      case AppEvents.PURCHASE_COMPLETED:
        const purchase = await api.call.purchases.get(action.payload?.data.purchaseId)
        console.log(purchase)
        if (purchase.status === PurchaseStatus.COMPLETED) {
          startRoll(purchase.metadata.index, 5000, 5)
        }
        break;
    }
  },
})


onBeforeUnmount(events)

defineExpose({ show, hide })
const popup = ref<InstanceType<typeof PopupWrapper> | null>(null)

</script>